// Central pricing configuration - Single source of truth
// All prices in smallest currency unit (paise for INR)

export const CURRENCY = 'INR' as const;

export const CREDIT_COSTS = {
  webSearch: 1,
  research: 3,
  discovery: 1,
  docParse: 1,
} as const;

export const TOPUP_PACK = {
  amount: 49900, // ₹499 in paise
  credits: 25,
  currency: 'INR' as const,
  displayPrice: '₹499',
} as const;

export const PRICING = {
  starter: {
    id: 'starter',
    name: 'Starter',
    price: 0,
    priceDisplay: '₹0',
    period: '/forever',
    currency: 'INR',
    description: 'Get started with AI-powered intelligence',
    credits: 0,
    limits: {
      signals: -1,
      entities: 5,
      alerts: 3,
      webSearches: 0,
      researchCredits: 0,
      signalDiscovery: 0,
      founderUpdates: 3,
      teamSeats: 1,
      documents: 0,
    },
    features: [
      'Unlimited AI conversations',
      '5 watchlist entities',
      '3 alert rules',
      'Deal pipeline & portfolio',
      'Morning briefs',
      'Email support',
    ],
    cta: 'Start Free',
    popular: false,
  },
  growth: {
    id: 'growth',
    name: 'Growth',
    price: 299900, // ₹2,999 in paise
    priceDisplay: '₹2,999',
    period: '/month',
    currency: 'INR',
    description: 'For serious fund operations',
    credits: 25,
    limits: {
      signals: -1,
      entities: 50,
      alerts: 25,
      webSearches: -1, // credit-gated
      researchCredits: -1, // credit-gated
      signalDiscovery: -1, // credit-gated
      founderUpdates: -1,
      teamSeats: 5,
      documents: -1,
    },
    features: [
      'Everything in Starter',
      '25 intelligence credits/mo',
      '50 watchlist entities',
      '25 alert rules',
      'Fund administration',
      'LP Portal',
      'Waterfall engine',
      'Multi-currency',
      'API access',
      'Priority support',
    ],
    cta: 'Start Free Trial',
    popular: true,
  },
  fund: {
    id: 'fund',
    name: 'Fund',
    price: 1499900, // ₹14,999 in paise
    priceDisplay: '₹14,999',
    period: '/month',
    currency: 'INR',
    description: 'For teams and institutions',
    credits: 150,
    limits: {
      signals: -1,
      entities: -1,
      alerts: -1,
      webSearches: -1,
      researchCredits: -1,
      signalDiscovery: -1,
      founderUpdates: -1,
      teamSeats: -1,
      documents: -1,
    },
    features: [
      'Everything in Growth',
      '150 intelligence credits/mo',
      'Unlimited entities & alerts',
      'Unlimited team seats',
      'Daily auto-discovery',
      'Custom webhooks',
      'SSO & SAML',
      'Dedicated support',
    ],
    cta: 'Contact Sales',
    popular: false,
  },
} as const;

// Legacy aliases for backward compatibility
export const LEGACY_PLAN_MAP: Record<string, PlanId> = {
  free: 'starter',
  pro: 'growth',
  enterprise: 'fund',
  starter: 'starter',
  growth: 'growth',
  fund: 'fund',
};

export type PlanId = keyof typeof PRICING;
export type PlanConfig = typeof PRICING[PlanId];

// Normalize plan ID from legacy or current
export function normalizePlanId(planId: string): PlanId {
  return LEGACY_PLAN_MAP[planId] || 'starter';
}

// Helper to get plan limits
export function getPlanLimits(planId: string) {
  const normalized = normalizePlanId(planId);
  return PRICING[normalized].limits;
}

// Helper to get plan credits
export function getPlanCredits(planId: string): number {
  const normalized = normalizePlanId(planId);
  return PRICING[normalized].credits;
}

// Helper to check if a limit is reached
export function isLimitReached(used: number, limit: number): boolean {
  if (limit === -1) return false;
  return used >= limit;
}

// Helper to get usage percentage
export function getUsagePercent(used: number, limit: number): number {
  if (limit === -1) return 0;
  if (limit === 0) return 100;
  return Math.min(100, Math.round((used / limit) * 100));
}

// Format currency for display (INR)
export function formatPrice(paise: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(paise / 100);
}

// For Razorpay - amounts in paise
export function getPriceInPaise(planId: PlanId): number {
  return PRICING[planId].price;
}

// Cost estimates per event (USD for internal tracking)
export const COST_ESTIMATES = {
  perplexityResearch: 0.05,
  perplexitySearch: 0.02,
  perplexityDiscovery: 0.02,
  lovableAI: 0,
  supabase: 0,
} as const;
